import { ArrowLeft, ArrowRight, Check, Loader2, X } from "lucide-react";

import { cn } from "@/lib/utils";

// @ts-ignore
import licitigoLogo from "../../../assets/licitigo_logo.svg";

export type Tone = "blue" | "emerald";

/**
 * Marco de pantalla completa del onboarding: fondo, marca y, si se puede salir,
 * la X. Solo modo claro, como el wizard al que reemplaza.
 */
export function OnboardingShell({
  children,
  onClose,
}: {
  children: React.ReactNode;
  /** Sin `onClose` no hay X: la primera vez no se sale hasta guardar la línea. */
  onClose?: () => void;
}) {
  return (
    // El `--background` claro del dashboard, fijo: con `bg-background` el modo
    // oscuro pintaría fondo oscuro bajo tarjetas pensadas en claro.
    <div className="min-h-screen bg-[#f1f5f9] text-slate-900">
      <div className="mx-auto flex min-h-screen w-full max-w-[1120px] flex-col px-4 pb-36 pt-5 sm:px-8 sm:pb-10 sm:pt-8">
        <div className="flex items-center justify-between">
          <img
            src={licitigoLogo}
            alt="Licitigo"
            className="h-7 w-auto lg:h-8"
          />
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Salir"
              className="flex size-9 cursor-pointer items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-white hover:text-slate-800"
            >
              <X className="size-4.5" strokeWidth={2.2} />
            </button>
          )}
        </div>
        {/* En escritorio el contenido va centrado en el alto; si no cabe, crece y se scrollea.
            En celular un paso se centra solo con `my-auto`. */}
        <div className="mt-6 flex flex-1 flex-col sm:mt-10 lg:mt-0 lg:justify-center lg:py-10">
          {children}
        </div>
      </div>
    </div>
  );
}

const EYEBROW_TONE: Record<Tone, string> = {
  blue: "text-blue-700",
  emerald: "text-emerald-600",
};

/** Rótulo en mayúsculas sobre el título: "PREGUNTA 1 DE 2". */
export function Eyebrow({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: Tone;
}) {
  return (
    <p
      className={cn(
        "font-mono text-[11px] font-semibold uppercase tracking-[0.14em]",
        EYEBROW_TONE[tone],
      )}
    >
      {children}
    </p>
  );
}

export function ScreenTitle({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h1
      className={cn(
        "mt-2 text-[32px] font-extrabold leading-[1.05] tracking-[-0.035em] text-slate-900 sm:text-[46px]",
        className,
      )}
    >
      {children}
    </h1>
  );
}

export function ScreenLead({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-3 max-w-[620px] text-[15px] leading-[1.55] text-slate-500">
      {children}
    </p>
  );
}

const CHIP_SELECTED: Record<Tone, string> = {
  blue: "border-blue-700 bg-blue-700 text-white",
  emerald: "border-emerald-600 bg-emerald-600 text-white",
};

/** Opción redonda que se marca y desmarca (rubros, productos, departamentos). */
export function Chip({
  children,
  selected,
  onClick,
  tone,
}: {
  children: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  tone: Tone;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-5 py-2.5 text-[14.5px] font-semibold transition-colors",
        selected
          ? CHIP_SELECTED[tone]
          : "border-slate-200 bg-white text-slate-800 hover:border-slate-300",
      )}
    >
      {selected && <Check className="size-3.5" strokeWidth={3} />}
      {children}
    </button>
  );
}

/**
 * Acciones de la pantalla. Hasta `lg` quedan fijas al pie de la pantalla, donde
 * llega el pulgar; en escritorio, en línea bajo el contenido.
 */
export function ActionBar({
  onBack,
  children,
}: {
  onBack?: () => void;
  children?: React.ReactNode;
}) {
  return (
    // Celular: fija al pie. Desde tablet, pegajosa: va bajo el contenido y, si es largo,
    // se queda visible al fondo mientras se hace scroll.
    <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200/70 bg-[#f1f5f9]/95 px-4 py-3 backdrop-blur sm:sticky sm:mt-8 sm:border-0 sm:px-0 sm:py-4">
      {/* En celular: Atrás arriba y la acción principal al fondo, donde llega el pulgar.
          Desde tablet: Atrás a la izquierda y las acciones a la derecha. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-[10px] px-3 py-2.5 text-[14px] font-semibold text-slate-500 transition-colors hover:text-slate-900 sm:mr-auto"
          >
            <ArrowLeft className="size-4" strokeWidth={2.4} />
            Atrás
          </button>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:gap-4">
          {children}
        </div>
      </div>
    </div>
  );
}

const ACTION_TONE: Record<Tone, string> = {
  blue: "bg-blue-700 hover:bg-blue-800",
  emerald: "bg-emerald-600 hover:bg-emerald-700",
};

export function ActionButton({
  children,
  onClick,
  tone,
  disabled,
  loading,
  arrow = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  tone: Tone;
  disabled?: boolean;
  loading?: boolean;
  /** Flecha al final: los "Continuar" la llevan. */
  arrow?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        "inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-full px-8 py-3.5 text-[15px] font-bold text-white shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto",
        ACTION_TONE[tone],
      )}
    >
      {loading && <Loader2 className="size-4 animate-spin" />}
      {children}
      {arrow && !loading && <ArrowRight className="size-4" strokeWidth={2.4} />}
    </button>
  );
}

/** Mensaje de error en línea, con la salida a la app si el flujo la permite. */
export function InlineError({
  message,
  onEscape,
}: {
  message: string;
  onEscape?: () => void;
}) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13.5px] font-semibold text-red-600">
      <span>{message}</span>
      {onEscape && (
        <button
          type="button"
          onClick={onEscape}
          className="cursor-pointer text-slate-500 underline underline-offset-4 hover:text-slate-800"
        >
          Entrar a la app sin configurar
        </button>
      )}
    </div>
  );
}
